/**
 * Firestore data model for Workbench.
 *
 * The store is fully denormalized — every document holds the fields it needs
 * to render or filter on its own. We do NOT use collection groups; every
 * collection here is top-level and queried by `taskId` / `ownerId` / `status`.
 */

// ---------- Users ----------

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  createdAt: Date;
  lastLoginAt: Date;
  isAdmin?: boolean;
  isReviewer?: boolean;
  /**
   * Campaigns this user is allowed to review. Ignored when `isAdmin` is true
   * (admins can review any campaign).
   */
  reviewCampaignIds?: string[];
  trustedCampaignIds?: string[];
  /**
   * Aligned `user_accounts` doc id for this user, looked up once on sign-in
   * via the `linkExpertId` callable. `null` means we looked and didn't find a
   * match; `undefined` means we haven't tried yet.
   */
  expertId?: string | null;
  /**
   * Per-expert unsubscribe token synced from Aligned alongside `expertId`.
   * Consumed server-side by the email sender to build the unsubscribe link;
   * never written from the client.
   */
  unsubscribeToken?: string | null;
  /**
   * Per-user notification preferences. Undefined is treated as enabled so
   * pre-existing users default to receiving notifications.
   */
  notifyOnApproved?: boolean;
  notifyOnChangesRequested?: boolean;
  /** Server-refreshed daily snapshot of the AI warning top quartile. */
  aiDetectionTopWarningQuartile?: boolean;
}

// ---------- Campaigns ----------

/**
 * One task type attached to a campaign, with its desired pool size. Campaigns
 * can have zero, one, or many types — each entry is managed independently via
 * `adjustCampaignTasks`. A type's entry is locked against removal while tasks
 * of that type still exist for the campaign.
 */
/** Per-task-type LLM review configuration. Lives on each CampaignTaskType
 *  entry so the same campaign can enable AI pre-review for some of its
 *  bound task types and not others. Only task types with a registered
 *  server-side reviewer do anything when this is enabled. */
export type { CampaignLlmReview } from '../domain/campaigns/model';

/** Per-task-type LLM pre-submit validator configuration. Parallel to
 *  CampaignLlmReview. When enabled, the work UI shows a Validate button
 *  next to Submit; the validator runs synchronously and returns advisory
 *  field-anchored comments inline (transient — never written to Firestore).
 *  Validators NEVER block submission. Only task types with a registered
 *  server-side validator do anything when this is enabled. */
export type { CampaignLlmValidator } from '../domain/campaigns/model';

/** Campaign-wide acknowledgement email configuration. */
export type { CampaignSubmissionEmail } from '../domain/campaigns/model';

/** Campaign-wide review-routing configuration. */
export type { CampaignAutoApprove } from '../domain/campaigns/model';

export type { CampaignAutoReleaseStaleClaims } from '../domain/campaigns/model';

export type { CampaignAutoReleaseStaleInProgress } from '../domain/campaigns/model';

export type { CampaignTaskType } from '../domain/campaigns/model';

export type { CampaignTaskDomain } from '../domain/campaigns/model';

/**
 * A campaign groups a batch of tasks with its own queue and review pool.
 * Every Task carries `campaignId` (and the denormalized `campaignName`);
 * the queue page subscribes to one campaign at a time.
 */
export type { Campaign } from '../domain/campaigns/model';

// ---------- Task Type Settings ----------

export type TaskTypeInstructionsKind = 'google-doc-url' | 'markdown';

/**
 * Admin-managed instructions attached to a task type and shown from the task
 * shell. `value` is either the Google Doc URL or the markdown body depending
 * on `kind`.
 */
export interface TaskTypeInstructions {
  kind: TaskTypeInstructionsKind;
  value: string;
}

export interface TaskTypeSettings {
  taskTypeId: string;
  instructions: TaskTypeInstructions | null;
  updatedAt: Date | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

// ---------- Tasks ----------

/**
 * Lifecycle of a task in the queue:
 *   available
 *     ↓ annotator claims
 *   claimed → in_progress
 *     ↓ annotator submits
 *   in_review
 *     ↓ reviewer picks up
 *   in_review (with reviewerId set)
 *     ↓ reviewer decides
 *   changes_requested  →  back to original owner, with the requesting
 *                         reviewer reserved for the next submission
 *   approved          →  terminal
 *   archived          →  terminal (admin)
 */
export type TaskStatus =
  | 'available'
  | 'claimed'
  | 'in_progress'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'archived';

export const TASK_STATUSES: TaskStatus[] = [
  'available',
  'claimed',
  'in_progress',
  'in_review',
  'changes_requested',
  'approved',
  'archived',
];

export interface Task {
  id: string;

  // Scoping — every task belongs to exactly one campaign.
  campaignId: string;
  campaignName: string;

  // Display
  /**
   * The task's label. Admins seed it at creation (via `adjustCampaignTasks`);
   * the current owner can edit it while they hold the task — see the
   * `updateTaskTitle` service and the matching Firestore rule disjunct.
   */
  title: string;
  description: string;
  /** Free-form payload the annotator works on (prompt, item to label, etc.). */
  payload: Record<string, unknown>;

  /**
   * Task type id — selects the UI component + result shape + validator.
   * Tasks created before the type system was introduced lack this field and
   * resolve to the built-in `legacy` module at read time.
   */
  taskType: string;
  /** Optional campaign/task-type domain split, e.g. math or physics. */
  domainId?: string | null;
  domainLabel?: string | null;

  /**
   * Stable identifier from the admin upload that created or last updated
   * this task. Unique within `(campaignId, taskType)`; present only on
   * tasks from payload-required types. Used by `uploadTasksFromPayloads`
   * to upsert idempotently across re-uploads.
   */
  uploadKey?: string;

  // Workflow state
  status: TaskStatus;
  priority: number;
  tags: string[];

  // Denormalized actor pointers — every document carries the names it needs
  // for display so the queue list never has to fetch user docs.
  createdBy: string;
  createdByName: string;

  ownerId: string | null;        // current annotator
  ownerName: string | null;
  ownerEmail?: string | null;
  /**
   * Aligned `user_accounts` doc id of the annotator who claimed this task.
   * Copied from the owner's user profile at claim time; `null` until claimed
   * or when the owner has no linked Aligned account.
   */
  expertId: string | null;

  reviewerId: string | null;     // current reviewer (set on pickup)
  reviewerName: string | null;
  /** Latest escalation metadata, denormalized for review-queue rendering. */
  escalatedAt?: Date | null;
  escalatedReviewRound?: number | null;
  escalatedById?: string | null;
  escalatedByName?: string | null;
  escalatedByEmail?: string | null;
  escalationNote?: string | null;

  // Annotator's submitted answer (latest revision)
  result: Record<string, unknown> | null;
  /** Transient browser-only warning when Storage-backed result content could
   *  not be hydrated. Never read from or written to Firestore. */
  resultLoadError?: string | null;

  // Denormalized counters for cheap list rendering
  commentCount: number;
  reviewRound: number;           // increments each time it re-enters review

  /**
   * Pointer to the bytes the reviewer signed off on. Stamped at approval time
   * by the reviewer command (derived via the task type module's
   * `deriveApprovedSubmission` hook); absent on task types that don't ship
   * uploaded artifacts. Use this — not `result.repoFiles` — when bundling the
   * approved submission for downstream clients, since `result` can be
   * overwritten by a later rework cycle while this captures the bytes at the
   * moment of approval.
   */
  approvedSubmission?: ApprovedSubmissionLocation;

  createdAt: Date;
  updatedAt: Date;
  claimedAt: Date | null;
  submittedAt: Date | null;
  reviewedAt: Date | null;
}

/**
 * Where the bytes for an approved submission live in Firebase Storage.
 * Generic on purpose — any task type that uploads files can populate this via
 * `TaskTypeModule.deriveApprovedSubmission`. `storagePathPrefix` is the
 * directory under which every file lives; listing that prefix gives the full
 * bundle.
 */
export interface ApprovedSubmissionLocation {
  storagePathPrefix: string;
  uploadSessionId: string;
  uploadedByUid: string;
  uploadedAt: string;
  fileCount: number;
}

// ---------- Comments ----------

/**
 * Top-level collection (not a subcollection) so we can query a user's
 * activity across tasks without a collection group.
 */
export type TaskCommentKind = 'note' | 'submission' | 'changes_requested' | 'approved' | 'field';

/** Who authored the comment. `undefined` on older docs is treated as `'human'`. */
export type TaskCommentSource = 'human' | 'llm';

/** Lifecycle state of an LLM-authored comment as it moves from raw AI output
 *  (pending) to something the human reviewer has explicitly blessed
 *  (approved). Only approved LLM comments feed the changes-requested email. */
export type LlmCommentStatus = 'pending' | 'approved';

/** Severity tag emitted by the LLM reviewer — purely advisory to the human. */
export type LlmCommentSeverity = 'info' | 'warning' | 'error';

/**
 * Stable identity for a commentable rubric field. The numeric `fieldPath`
 * remains as a navigation/cache field for compatibility, but it is not the
 * identity of a requirement: array positions change when writers insert or
 * remove requirements.
 */
export type TaskCommentFieldAnchor =
  | {
      type: 'rubric-requirement';
      requirementId: string;
      field: 'requirement' | 'sources' | 'quote' | 'justification' | 'score';
    }
  | {
      type: 'rubric-grade';
      requirementId: string;
      modelKey: string;
      field: 'grade';
    };

/** A migrated legacy comment needs a human check, or no longer has a target. */
export type TaskCommentFieldAnchorState = 'needs-review' | 'orphaned';

export interface TaskComment {
  id: string;

  taskId: string;
  // Denormalized so a comment list can render task context without joins.
  taskTitle: string;

  authorId: string;
  authorName: string;

  text: string;

  /** Tags the comment with the workflow event it accompanies, if any. */
  kind: TaskCommentKind;

  /**
   * Dotted path into the task's result targeting the field this comment is
   * about (e.g. `rating`, `steps.0.description`). `null` for task-level
   * comments (status-change summaries, free-form notes).
   */
  fieldPath: string | null;

  /**
   * Stable rubric identity. Optional for non-rubric and legacy comments.
   * `fieldPath` is deliberately retained because generic task-shell code and
   * existing exports still use dotted paths for display and navigation.
   */
  fieldAnchor?: TaskCommentFieldAnchor;

  /** Set only by the legacy migration when the old position is uncertain. */
  fieldAnchorState?: TaskCommentFieldAnchorState;

  /**
   * Threading pointer. `null` for root comments; a root's id for replies.
   * Only root comments carry the addressed/resolved state and show up in the
   * per-field pin count — replies are conversation, not findings.
   */
  parentCommentId: string | null;

  /**
   * The task's `reviewRound` at the time the comment was written. Lets the
   * comment history segment by round even after rework cycles.
   */
  reviewRound: number;

  /** Expert marks field comments as addressed during rework. */
  addressed: boolean;
  addressedBy: string | null;
  addressedByName: string | null;
  addressedAt: Date | null;

  /**
   * Snapshot of the field value when the reviewer last sent the task back
   * for rework. Reviewers/admins use this on the next review round to diff
   * what changed in response to the comment.
   */
  fieldSnapshot?: TaskCommentFieldSnapshot;

  createdAt: Date;

  // ---- LLM comment fields (absent on human comments) ----

  /** Absent ⇒ human. Written only by the onTaskEnterReview trigger. */
  source?: TaskCommentSource;

  /** Only meaningful when source === 'llm'. Pending = raw AI output the
   *  human hasn't blessed yet; approved = human reviewer confirmed. */
  llmStatus?: LlmCommentStatus;

  /** Model id the reviewer ran on, for provenance in audits. */
  model?: string;

  /** Reviewer plugin and model-policy versions for reproducible AI output. */
  reviewerPluginId?: string;
  reviewerPluginVersion?: number;
  modelPolicyVersion?: string;

  /** LLM-advisory severity. Human comments don't carry this. */
  severity?: LlmCommentSeverity;

  // ---- Edit provenance (set when a human reviewer edits any comment's text) ----

  editedBy?: string | null;
  editedByName?: string | null;
  editedAt?: Date | null;
}

export interface TaskCommentFieldSnapshot {
  value: unknown;
  missing: boolean;
  capturedAt: Date | null;
  reviewRound: number;
}

// ---------- Activity log ----------

export type TaskActivityKind =
  | 'created'
  | 'claimed'
  | 'submitted'
  | 'review_assigned'
  | 'review_started'
  | 'review_released'
  | 'escalated'
  | 'changes_requested'
  | 'approved'
  | 'reopened'
  | 'archived';

export interface TaskActivity {
  id: string;

  taskId: string;
  taskTitle: string;

  actorId: string;
  actorName: string;
  /** Review assignment target, distinct from the admin performing the action. */
  reviewerId?: string | null;

  kind: TaskActivityKind;
  fromStatus: TaskStatus | null;
  toStatus: TaskStatus | null;

  createdAt: Date;
}
